import { z } from 'zod';
import type { DnaSourceKey } from './dna';

/* ─────────────────────────────────────────────────────────────────────
   Studio — nove agentes de produção. Cada um tem uma bancada própria,
   já abastecida pelo DNA: o catálogo diz o que cada agente consome,
   quanto custa e o que entrega.
   ───────────────────────────────────────────────────────────────────── */

export type StudioCategory = 'estrategia' | 'presenca' | 'narrativa';
export const STUDIO_CATEGORIES: ReadonlyArray<[StudioCategory, string]> = [
  ['estrategia', 'Estratégia'], ['presenca', 'Presença digital'], ['narrativa', 'Narrativa'],
];
export const STUDIO_TABS: ReadonlyArray<[StudioCategory | 'todas', string]> = [
  ['todas', 'Todas'], ['estrategia', 'Estratégia'], ['presenca', 'Presença'], ['narrativa', 'Narrativa'],
];

export interface StudioToolMeta {
  key: StudioToolKey;
  no: string;
  name: string;
  desc: string;
  category: StudioCategory;
  /** extensão da entrega, como se lê: "18–24 páginas" */
  ext: string;
  /** custo em tokens da organização */
  cost: number;
  formats: string;
  /** o que o agente lê do DNA, em ordem de importância */
  dna: readonly DnaSourceKey[];
  /** nome do documento que a bancada produz */
  deliverable: string;
  hero?: boolean;
  /** 'page' = a peça é um site: abre no canvas e pode ser publicada */
  output: 'document' | 'page';
}

export const STUDIO_TOOL_KEYS = ['plan', 'acao', 'apres', 'landing', 'site', 'brand', 'manif', 'ebook', 'camp'] as const;
export type StudioToolKey = (typeof STUDIO_TOOL_KEYS)[number];
export const studioToolKeySchema = z.enum(STUDIO_TOOL_KEYS);

export const STUDIO_TOOLS: readonly StudioToolMeta[] = [
  { key: 'plan', no: '01', name: 'Planejamento Estratégico', category: 'estrategia', hero: true,
    desc: 'Escreve o documento completo — objetivos, públicos, diretrizes e indicadores — a partir do DNA.',
    ext: '18–24 páginas', cost: 120, formats: 'PDF · DOCX', deliverable: 'Planejamento Estratégico', output: 'document',
    dna: ['proposito', 'triade', 'valores', 'diferenciais', 'gaps', 'horizontes'] },
  { key: 'acao', no: '02', name: 'Plano de Ação', category: 'estrategia',
    desc: 'Transforma as prioridades em iniciativas com responsável, prazo e custo, no formato 5W2H.',
    ext: '1 cronograma', cost: 80, formats: 'XLSX · PDF', deliverable: 'Plano de Ação', output: 'document',
    dna: ['gaps', 'horizontes', 'prioridades', 'macro', 'acoes'] },
  { key: 'apres', no: '03', name: 'Apresentação Institucional', category: 'estrategia',
    desc: 'Monta o deck de conselho: narrativa, números e as peças visuais da marca.',
    ext: '16–20 slides', cost: 140, formats: 'PPTX · PDF', deliverable: 'Apresentação Institucional', output: 'document',
    dna: ['proposito', 'visao', 'triade', 'diferenciais', 'bmc'] },
  { key: 'landing', no: '04', name: 'Landing Page', category: 'presenca',
    desc: 'Redige e diagrama uma página de conversão para uma causa, campanha ou matrícula.',
    ext: '1 página', cost: 95, formats: 'Publicada · versionada', deliverable: 'Landing Page', output: 'page',
    dna: ['proposito', 'diferenciais', 'triade', 'valores'] },
  { key: 'site', no: '05', name: 'Website', category: 'presenca',
    desc: 'Projeta a arquitetura do site inteiro e escreve o conteúdo de cada página.',
    ext: '5–7 páginas', cost: 210, formats: 'Publicado · versionado', deliverable: 'Website', output: 'page',
    dna: ['proposito', 'visao', 'valores', 'diferenciais', 'bmc', 'segmentos'] },
  { key: 'brand', no: '06', name: 'Brand Book', category: 'presenca',
    desc: 'Deriva cor, tipografia, tom de voz e regras de aplicação do posicionamento.',
    ext: '28–34 páginas', cost: 160, formats: 'PDF · Figma', deliverable: 'Brand Book', output: 'document',
    dna: ['proposito', 'valores', 'triade', 'diferenciais'] },
  { key: 'manif', no: '07', name: 'Manifesto', category: 'narrativa',
    desc: 'Destila o propósito num texto curto que a organização inteira consegue repetir.',
    ext: '1 página', cost: 45, formats: 'DOCX · PDF', deliverable: 'Manifesto', output: 'document',
    dna: ['proposito', 'valores', 'visao'] },
  { key: 'ebook', no: '08', name: 'E-book', category: 'narrativa',
    desc: 'Desenvolve um conteúdo educativo aprofundado sobre o que vocês dominam.',
    ext: '24–40 páginas', cost: 190, formats: 'PDF · EPUB', deliverable: 'E-book', output: 'document',
    dna: ['proposito', 'diferenciais', 'macro', 'horizontes'] },
  { key: 'camp', no: '09', name: 'Campanha', category: 'narrativa',
    desc: 'Cria o conceito e desdobra em post, story, banner e peças de apoio.',
    ext: '1 conceito + 8 peças', cost: 170, formats: 'Figma · PDF', deliverable: 'Campanha', output: 'document',
    dna: ['proposito', 'triade', 'valores', 'segmentos', 'diferenciais'] },
];

export const studioTool = (key: StudioToolKey) => STUDIO_TOOLS.find(t => t.key === key)!;
export const isPageTool = (key: string) => STUDIO_TOOLS.find(t => t.key === key)?.output === 'page';
export const studioCategoryLabel = (c: StudioCategory) => STUDIO_CATEGORIES.find(([k]) => k === c)![1];

/* ── briefing da bancada ──────────────────────────────────────────── */
export const PUBLICOS = ['Conselho diretor', 'Equipe interna', 'Parceiros e investidores', 'Famílias'] as const;
export const TONS = ['institucional', 'equilibrado', 'direto'] as const;
export const TOM_LABEL: Record<(typeof TONS)[number], string> = {
  institucional: 'Institucional', equilibrado: 'Equilibrado', direto: 'Direto',
};

const ctaLink = z.string().trim().max(400)
  .refine(v => v === '' || /^(https?:\/\/|mailto:|tel:|#)/i.test(v), 'use um link https://, mailto:, tel: ou #âncora');

export const briefingSchema = z.object({
  objective: z.string().trim().max(200).default(''),
  /** para páginas: para onde vai o botão principal (WhatsApp, formulário, agenda…) */
  link: ctaLink.default(''),
  audience:  z.enum(PUBLICOS).default('Conselho diretor'),
  tone:      z.enum(TONS).default('equilibrado'),
  notes:     z.string().trim().max(1200).default(''),
});
export type Briefing = z.infer<typeof briefingSchema>;

export const generateRequestSchema = z.object({
  tool: studioToolKeySchema,
  briefing: briefingSchema,
});
export type GenerateRequest = z.infer<typeof generateRequestSchema>;
